import { Badge, Group, LoadingOverlay, Switch } from '@mantine/core';
import { find } from 'lodash/fp';
import React, { useMemo } from 'react';
import { Row } from 'react-table';

import {
  CustomerWithControlledType,
  useConfig,
  useIsChannelPartner,
  useSupportCenterCustomers,
} from '@portals/api/partners';
import { useCurrentUser } from '@portals/api/ui';
import { ModalButton, usePortalProtocolAndDomain } from '@portals/framework';
import {
  RouteModalLink,
  useOpenRouteModal,
} from '@portals/framework/route-modals';
import { ReactComponent as AddSquare } from '@portals/icons/linear/add-square.svg';
import { ReactComponent as Danger } from '@portals/icons/linear/danger.svg';
import { ReactComponent as Export2 } from '@portals/icons/linear/export-2.svg';
import { ReactComponent as MonitorMobbile } from '@portals/icons/linear/monitor-mobbile.svg';
import { ReactComponent as Ticket } from '@portals/icons/linear/ticket.svg';
import { ReactComponent as UserSquare } from '@portals/icons/linear/user-square.svg';
import {
  AvatarCell,
  RowMenuItem,
  RowMenuItems,
  SmartTable,
  TextWithIconCell,
} from '@portals/table';
import {
  PaginatedFilterTypeEnum,
  RowMenuRefType,
  TableColumn,
} from '@portals/types';

import { useUsersWithSupportSeats } from '../settings/support/support.hooks';
import { useSupportState } from '../support-overview/SupportStateProvider';

export function OrganizationsTable() {
  const { isLabIncluded, setIsLabIncluded } = useSupportState();
  const customers = useSupportCenterCustomers(isLabIncluded);
  const config = useConfig();

  const members = useUsersWithSupportSeats();

  const currentUser = useCurrentUser();
  const controlled = currentUser?.data?.accessible_tenants?.organizations;

  const isChannelPartner = useIsChannelPartner();

  const customersWithControlled = useMemo(
    () =>
      customers.data?.map((customer) => ({
        ...customer,
        controlled: find({ id: customer.id }, controlled) !== undefined,
      })),
    [customers, controlled]
  );

  const { protocolAndDomain } = usePortalProtocolAndDomain();
  const openRouteModal = useOpenRouteModal();

  const columns = useMemo(() => {
    const tableColumns: Array<TableColumn<CustomerWithControlledType>> = [
      {
        dataField: 'name',
        text: 'Name',
        sort: true,
        isSticky: true,
        minWidth: 500,
        maxWidth: 500,
        filter: {
          type: PaginatedFilterTypeEnum.Text,
        },
        formatter: (_, { name, id, logo_url, lab }) => (
          <Group align="center" noWrap>
            <RouteModalLink modalId="customer" pathParams={[id]}>
              <AvatarCell src={logo_url} label={name} radius="md" />
            </RouteModalLink>

            {lab ? (
              <Badge
                color="indigo_accent"
                variant="filled"
                size="lg"
                fw={300}
                sx={{ flexShrink: 0 }}
              >
                Lab account
              </Badge>
            ) : null}
          </Group>
        ),
      },
      {
        dataField: 'devices_count',
        text: 'Devices',
        sort: true,
        filter: {
          type: PaginatedFilterTypeEnum.Number,
        },
        formatter: (_, row) => (
          <TextWithIconCell
            Icon={MonitorMobbile}
            label={row.devices_count.toString()}
            spacing="xs"
            color="blue_gray"
          />
        ),
      },
      {
        dataField: 'incidents_count',
        text: 'Incidents',
        sort: true,
        filter: {
          type: PaginatedFilterTypeEnum.Number,
        },
        formatter: (_, row) => (
          <TextWithIconCell
            Icon={Danger}
            label={row.incidents_count.toString()}
            spacing="xs"
            color="blue_gray"
          />
        ),
      },
      {
        dataField: 'tickets_count',
        text: 'Tickets',
        sort: true,
        filter: {
          type: PaginatedFilterTypeEnum.Number,
        },
        formatter: (_, row) => (
          <TextWithIconCell
            Icon={Ticket}
            label={row.tickets_count.toString()}
            spacing="xs"
            color="blue_gray"
          />
        ),
      },
    ];

    if (isChannelPartner) {
      tableColumns.push({
        dataField: 'manufacturer',
        text: 'Manufacturer',
        sort: true,
        filter: {
          type: PaginatedFilterTypeEnum.Text,
        },
        formatter: (_, row) => (
          <AvatarCell
            src={row.main_partner.logo_url}
            label={row.main_partner.display_name}
            radius="md"
            withAbbreviation={false}
          />
        ),
      });
    }

    return tableColumns;
  }, [isChannelPartner]);

  const getMenuItems = (
    row: Row<CustomerWithControlledType>,
    menuRef: RowMenuRefType
  ) => {
    const items: Array<RowMenuItem> = [
      {
        id: 'view',
        onClick: () => {
          openRouteModal({
            modalId: 'customer',
            pathParams: [row.original.id],
          });
          menuRef.onClose();
        },
        label: 'View Customer',
        Icon: UserSquare,
      },
    ];

    if (row.original.controlled) {
      items.push({
        id: 'visit',
        onClick: () =>
          window.open(
            `${protocolAndDomain}?org=${row.original.id}`,
            `_blank`,
            'noopener noreferrer'
          ),
        label: 'Visit Portal',
        Icon: Export2,
      });
    }

    return items;
  };

  return (
    <>
      <LoadingOverlay visible={!customers.isFetched} />

      {customers.isFetched ? (
        <SmartTable<CustomerWithControlledType>
          keyField="id"
          name="partners.customers"
          data={customersWithControlled}
          columns={columns}
          additionalActions={() => (
            <>
              <Switch
                label="Include lab customers"
                checked={isLabIncluded}
                onChange={(e) => setIsLabIncluded(e.target.checked)}
              />
              {config.data?.partner?.classification === 'channel_partner' &&
              members?.length > 0 ? (
                <ModalButton
                  modalName="AddOrganization"
                  label="Invite Customer"
                  leftIcon={<AddSquare />}
                />
              ) : null}
            </>
          )}
          isUrlSyncEnabled={false}
          noDataIndication={{ title: 'No customers' }}
          menuProps={{ position: 'left' }}
          rowMenu={({ row, wrapperProps }) => (
            <RowMenuItems
              wrapperProps={wrapperProps}
              labelSize="sm"
              items={getMenuItems(row, wrapperProps.menuRef)}
            />
          )}
        />
      ) : null}
    </>
  );
}
